import React from 'react'
import Highlighter from 'react-highlight-words'

import { useBoolean } from '@guidde-co/shared.hooks.use-boolean'

import makeStyles from '@material-ui/core/styles/makeStyles'

import Box from '@material-ui/core/Box'
import TextField from '@material-ui/core/TextField'
import Typography from '@material-ui/core/Typography'
import Tooltip from '@material-ui/core/Tooltip'
import CircularProgress from '@material-ui/core/CircularProgress'
import IconButton from '@material-ui/core/IconButton'

const SliceIcon = () => (
    <svg
        version="1.1"
        x="0px"
        y="0px"
        viewBox="0 0 512 512"
        width="10px"
        height="10px"
        fill="white"
    >
        <g>
            <g>
                <path
                    d="M512,107.275c-23.658-33.787-70.696-42.691-104.489-19.033L233.753,209.907l-63.183-44.246
			c23.526-40.618,12.46-93.179-26.71-120.603c-41.364-28.954-98.355-18.906-127.321,22.45
			c-28.953,41.358-18.913,98.361,22.452,127.327c28.384,19.874,64.137,21.364,93.129,6.982l77.388,54.185l-77.381,54.179
			c-28.992-14.375-64.743-12.885-93.129,6.982c-41.363,28.966-51.404,85.963-22.452,127.32
			c28.966,41.363,85.963,51.411,127.32,22.457c39.165-27.424,50.229-79.985,26.71-120.603l63.183-44.246l173.751,121.658
			c33.793,23.665,80.831,14.755,104.489-19.033l-212.41-148.715L512,107.275z M91.627,167.539
			c-26.173,0-47.392-21.219-47.392-47.392s21.22-47.392,47.392-47.392c26.179,0,47.392,21.219,47.392,47.392
			S117.806,167.539,91.627,167.539z M91.627,439.253c-26.173,0-47.392-21.219-47.392-47.392c0-26.173,21.219-47.392,47.392-47.392
			c26.179,0,47.392,21.219,47.392,47.392C139.019,418.033,117.806,439.253,91.627,439.253z"
                />
            </g>
        </g>
    </svg>
)

export const formatTime = (seconds: number) => {
    const format = (val: number) => `0${Math.floor(val)}`.slice(-2)

    const hours = seconds / 3600
    const minutes = (seconds % 3600) / 60
    const secs = seconds % 60

    return [...(hours >= 1 ? [hours] : []), minutes, secs].map(format).join(':')
}

const formatDuration = (duration: number, rangeValues: any) => {
    const start = (duration / 100) * rangeValues?.[0]
    const end = (duration / 100) * rangeValues?.[1]

    if ((start || start === 0) && end) {
        return `${formatTime(start)}`
    }

    return ''
}

const useStyles = makeStyles({
    timeLineItem: {
        minHeight: '90px',
        '&:before': {
            display: 'none'
        }
    },
    timeLineContent: {
        padding: '6px 0px 6px 16px',
        width: '100%',
        position: 'absolute',
        top: '-35%',
        left: '8px',
        cursor: 'pointer'
    },
    ChapterTitle: {
        display: '-webkit-box',
        '-webkit-line-clamp': 1,
        '-webkit-box-orient': 'vertical',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        width: '100%',
        fontSize: '16px !important',
        fontWeight: 'bold'
    },
    timeText: {
        fontSize: '12px !important',
        cursor: 'pointer',
        color: '#CB0000',
        fontWeight: 400
    },
    textField: {
        width: '100%',
        '& .MuiInputBase-root.Mui-disabled': {
            color: 'inherit'
        }
    },
    text: {
        fontSize: '12px',
        fontWeight: 500
    },
    subtitleBackground: {
        position: 'relative',
        borderRadius: '4px',
        cursor: 'pointer',
        border: ({ isActive, searchString }: Highlight) =>
            isActive && !searchString?.trim()?.length
                ? 'solid #FED243 1px'
                : 'solid #E0E0E0 1px',
        background: ({ isActive, searchString }: Highlight) =>
            isActive && !searchString?.trim()?.length
                ? '#FAFAFA'
                : 'transparent'
    },
    export: {
        position: 'absolute',
        top: '-20px',
        right: '5px'
    }
})
export type Highlight = {
    /**
     * Active state of the current chapter
     */
    isActive: boolean
    /**
     * The actual search string
     */
    searchString: string
}

export type ChapterContentProps = {
    /**
     * whether this chapters is selected
     */
    isActive: boolean
    /**
     * on click handler
     */
    onClick: () => void
    /**
     * chapter during view mode (taken from slicingSuggestion property)
     */
    chapter: {
        /**
         * chapter's title
         */
        title: string
        /**
         * start point in seconds
         */
        start: number
        /**
         * end point in seconds
         */
        end: number
        /**
         * url link
         */
        thumbnail?: string
        /**
         * available during edit mode ony (taken from redux): 1st number - video start percentage, 2nd - video end percentage
         */
        rangeValues?: Array<number>
    }
    /**
     * video duration to calculate time in edit mode
     */
    duration?: number
    /**
     * edit or view mode
     */
    isEditMode?: boolean
    /**
     * during export
     */
    isLoading?: boolean
    /**
     * handler to create new playbook
     */
    onExportClick?: () => void
    /**
     * change title during editing
     */
    onTitleChange?: (_title: string) => void
    /**
     * user searched string
     */
    searchString?: string
}

const MIN_SEARCH_LENGTH = 2

export function ChapterContent({
    isActive,
    isEditMode,
    isLoading,
    chapter,
    duration,
    onExportClick,
    onClick,
    onTitleChange,
    searchString = ''
}: ChapterContentProps) {
    const classes = useStyles({ isActive, searchString })

    const hovered = useBoolean()

    return (
        <Box
            onMouseEnter={hovered.setTrue}
            onMouseLeave={hovered.setFalse}
            width="100%"
            onClick={onClick}
            id="container"
            data-cy="chapter-content"
        >
            <Box
                px={2}
                my={1.5}
                className={classes.subtitleBackground}
                width="100%"
                id="content-wrapper1"
                data-cy="content-wrapper1"
            >
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    width="100%"
                    height="54px"
                    id="content-wrapper2"
                    data-cy="content-wrapper2"
                >
                    <Box width="100%" mr={2} id="edit-wrapper">
                        {isEditMode ? (
                            <TextField
                                multiline
                                maxRows={3}
                                fullWidth
                                InputProps={{
                                    className: classes.ChapterTitle
                                }}
                                onClick={e => {
                                    // in order to prevent change to activePart
                                    e.stopPropagation()
                                }}
                                onChange={e => {
                                    onTitleChange?.(e.target.value)
                                }}
                                className={classes.textField}
                                value={chapter.title}
                                size="small"
                            />
                        ) : (
                            <Box
                                id="highlighter-wrapper"
                                data-cy="highlighter-wrapper"
                                style={{ fontSize: '16px', fontWeight: 600 }}
                            >
                                <Highlighter
                                    highlightStyle={{
                                        backgroundColor: '#FFF2C7'
                                    }}
                                    searchWords={
                                        searchString?.length <=
                                        MIN_SEARCH_LENGTH
                                            ? []
                                            : [searchString]
                                    }
                                    textToHighlight={chapter.title}
                                    className={classes.ChapterTitle}
                                />
                            </Box>
                        )}
                    </Box>

                    <Typography className={classes.timeText}>
                        {isEditMode
                            ? formatDuration(duration || 0, chapter.rangeValues)
                            : `${formatTime(chapter.start || 0)}`}
                    </Typography>
                </Box>
                <Box className={classes.export}>
                    {isEditMode && (
                        <Box pt={1}>
                            {isLoading ? (
                                <CircularProgress size="20px" />
                            ) : (
                                <Tooltip
                                    style={{
                                        opacity: hovered.isTrue ? 100 : 0
                                    }}
                                    title="Clip this chapter into a new video"
                                    enterDelay={500}
                                    interactive
                                    placement="top"
                                    arrow
                                >
                                    <IconButton
                                        disableRipple={true}
                                        disableFocusRipple={true}
                                        onClick={e => {
                                            e.stopPropagation()
                                            onExportClick?.()
                                        }}
                                        style={{
                                            background: '#090c10',
                                            borderRadius: '50%',
                                            zIndex: 1,
                                            padding: '5px'
                                        }}
                                        size="small"
                                    >
                                        <SliceIcon />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    )
}
